import myShopClient from '@services/nearlive';

import { useShop } from '@/hooks/useShop';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useInvalidateLocalPotential } from '@/hooks/useInvalidateLocalPotential';

/**
 * Drives the Unify subscribe / migration trigger (NEA-5751).
 *
 * `subscribe(plan?)` POSTs to the per-shop subscribe endpoint, which branches
 * server-side: a migration cohort (legacy NearSt sub on the customer) gets the
 * migration journey and the `plan` is ignored; a brand-new customer needs a
 * plan (`'starter'`/`'pro'`). On success we `mutate()` the billing-status cache
 * so the dashboard reflects the new subscription without waiting for a refocus.
 *
 * `isMigrationCohort` is a UX hint only — migrated shops keep `'nearst'` in
 * `apps` (NEA-5756) whereas brand-new Unify shops don't. It decides whether the
 * caller shows a plan picker; the server stays authoritative on the journey.
 */
export function useSubscribe() {
	const { shop } = useShop();
	const { mutate } = useBillingStatus(shop?.id);
	const invalidateScore = useInvalidateLocalPotential();

	const isMigrationCohort = Array.isArray(shop?.apps) && shop.apps.includes('nearst');

	const subscribe = async (plan) => {
		if (!shop?.id) throw new Error('No shop available to subscribe');
		const client = await myShopClient();
		const { data } = await client.post(`/api/billing/subscription/${shop.id}`, plan ? { plan } : {});
		// A plan change shifts the channel allowance, which changes the score.
		// Fired without awaiting — nothing reads the revalidated caches before
		// SWR settles them, and awaiting added over a second to the welcome CTA
		// (NEA-5853).
		Promise.all([mutate(), invalidateScore()]).catch(() => {});
		return data;
	};

	return { subscribe, isMigrationCohort };
}
